import * as THREE from 'three';
import { Engine } from './core/Engine';
import { InputManager } from './core/InputManager';
import { SceneManager } from './rendering/SceneManager';
import { CameraController } from './rendering/CameraController';
import { PhysicsWorld } from './physics/PhysicsWorld';
import { PlayerBike } from './entities/PlayerBike';
import { WeaponManager } from './weapons/WeaponManager';
import { Destructible } from './entities/Destructible';
import { Turret, TurretConfig } from './entities/Turret';
import { EnemyBike, EnemyBikeConfig } from './entities/EnemyBike';
import { FlyingDrone, spawnDroneSwarm } from './entities/FlyingDrone';
import { HeavyTank } from './entities/HeavyTank';
import { SniperTower } from './entities/SniperTower';
import { MegaMech } from './entities/MegaMech';
import { HUD } from './ui/HUD';
import { Radar } from './ui/Radar';
import { ProjectilePool } from './weapons/ProjectilePool';
import { EventBus } from './core/EventBus';
import { WorldGenerator } from './world/WorldGenerator';
import { audioManager } from './core/AudioManager';
import { soundEffects } from './core/SoundEffects';
import { EffectsManager } from './effects/EffectsManager';
import { WaveMode } from './modes/WaveMode';
import { TimeTrialMode } from './modes/TimeTrialMode';
import { MissionMode } from './modes/MissionMode';
import { ModeSelectMenu, GameModeType } from './ui/ModeSelectMenu';
import { MobileManager } from './core/MobileManager';

export class Game {
  private engine!: Engine;
  private inputManager!: InputManager;
  private sceneManager!: SceneManager;
  private cameraController!: CameraController;
  private physicsWorld!: PhysicsWorld;
  private playerBike!: PlayerBike;
  private weaponManager!: WeaponManager;
  private hud!: HUD;
  private radar!: Radar;
  private destructibles: Destructible[] = [];
  private turrets: Turret[] = [];
  private enemyBikes: EnemyBike[] = [];
  private drones: FlyingDrone[] = [];
  private tanks: HeavyTank[] = [];
  private sniperTowers: SniperTower[] = [];
  private megaMech: MegaMech | null = null;
  private enemyProjectilePool!: ProjectilePool;
  private worldGenerator!: WorldGenerator;
  private effectsManager!: EffectsManager;

  // Game modes
  private waveMode: WaveMode | null = null;
  private timeTrialMode: TimeTrialMode | null = null;
  private missionMode: MissionMode | null = null;
  private modeSelectMenu: ModeSelectMenu | null = null;
  private currentMode: GameModeType = 'freeRoam';
  private isWaveModeActive: boolean = false;

  private isFiring: boolean = false;
  private isPaused: boolean = false;
  private pauseMenu: HTMLElement | null = null;

  // Player state
  private playerHealth: number = 200;
  private readonly MAX_PLAYER_HEALTH = 200;
  private isDead: boolean = false;
  private respawnTimer: number = 0;
  private readonly RESPAWN_DELAY = 3; // seconds

  public async init(): Promise<void> {
    // Initialize input manager
    this.inputManager = new InputManager();

    // Initialize Three.js scene
    const container = document.getElementById('game-container')!;
    this.sceneManager = new SceneManager(container);

    // Initialize physics
    this.physicsWorld = new PhysicsWorld();

    // Create player bike
    this.playerBike = new PlayerBike(
      this.sceneManager.scene,
      this.physicsWorld,
      new THREE.Vector3(0, 1, 0)
    );

    // Set up camera to follow player
    this.cameraController = new CameraController(this.sceneManager.camera);
    this.cameraController.setTarget(this.playerBike);

    // Initialize weapon manager
    this.weaponManager = new WeaponManager(
      this.sceneManager.scene,
      this.physicsWorld
    );

    // Create HUD
    this.hud = new HUD();

    // Create Radar
    this.radar = new Radar();

    // Create Effects Manager (particles, explosions, etc.)
    this.effectsManager = new EffectsManager(this.sceneManager.scene);

    // Create enemy projectile pool (separate from player weapons)
    this.enemyProjectilePool = new ProjectilePool(
      this.sceneManager.scene,
      this.physicsWorld
    );

    // Generate the world
    this.worldGenerator = new WorldGenerator(
      this.sceneManager.scene,
      this.physicsWorld,
      {
        cityConfig: {
          gridSize: 4,
          blockSize: 35,
          roadWidth: 12,
          minBuildingHeight: 10,
          maxBuildingHeight: 35,
          density: 0.6,
          centerClearRadius: 40,
        },
      }
    );
    this.worldGenerator.generate();

    // Initialize Game Modes
    const modeContext = {
      scene: this.sceneManager.scene,
      physicsWorld: this.physicsWorld,
      projectilePool: this.enemyProjectilePool,
      weaponManager: this.weaponManager,
      playerMesh: this.playerBike.getMesh(),
      getPlayerPosition: () => this.playerBike.getPosition(),
    };

    this.waveMode = new WaveMode(modeContext);
    this.timeTrialMode = new TimeTrialMode(modeContext);
    this.missionMode = new MissionMode(modeContext);
    this.modeSelectMenu = new ModeSelectMenu();

    // Create some destructible targets (for free roam only)
    this.spawnDestructibles();

    // Spawn enemies (for free roam only - wave mode handles its own enemies)
    this.spawnTurrets();
    this.spawnEnemyBikes();
    this.spawnDrones();
    this.spawnTanks();
    this.spawnSniperTowers();
    this.spawnBoss();

    // Set up event listeners for player damage
    this.setupDamageHandlers();

    // Initialize game engine
    this.engine = new Engine({
      fixedUpdate: this.fixedUpdate.bind(this),
      render: this.render.bind(this),
    });

    // Set up keyboard shortcuts
    this.setupControls();

    // Initialize touch controls for mobile
    this.initTouchControls(container);

    // Start background music (will play when user adds music file)
    this.initAudio();

    // Show mode selection menu on startup
    this.openModeSelect();
  }

  private initAudio(): void {
    // Browser autoplay policy requires user interaction first
    // Start music and engine sound on first click/keypress
    const startAudio = () => {
      audioManager.playMusic('/audio/music_main.mp3');
      soundEffects.startEngine();
      window.removeEventListener('click', startAudio);
      window.removeEventListener('keydown', startAudio);
      window.removeEventListener('touchstart', startAudio);
    };

    window.addEventListener('click', startAudio);
    window.addEventListener('keydown', startAudio);
    window.addEventListener('touchstart', startAudio);
  }

  private initTouchControls(container: HTMLElement): void {
    if (!MobileManager.isTouchDevice()) return;

    // Initialize touch controls via InputManager with pause callback
    this.inputManager.initTouchControls(container, () => {
      this.togglePause();
    });

    // Set up weapon switch callback (double-tap fire zone)
    this.inputManager.onTouchWeaponSwitch(() => {
      this.weaponManager.nextWeapon();
    });
  }

  private setupDamageHandlers(): void {
    // Listen for player damage events
    EventBus.on('player:damaged', (data) => {
      if (this.isDead) return;

      // Apply shield damage reduction if active
      const shieldMultiplier = this.weaponManager.getUpgradeManager().getShieldMultiplier();
      const finalDamage = data.damage * shieldMultiplier;

      this.playerHealth = Math.max(0, this.playerHealth - finalDamage);
      this.hud.updateHealth(this.playerHealth, this.MAX_PLAYER_HEALTH);

      if (this.playerHealth <= 0) {
        this.onPlayerDeath();
      }
    });

    // Listen for player heal events (from collectibles)
    EventBus.on('player:healed', (data) => {
      if (this.isDead) return;

      this.playerHealth = Math.min(this.MAX_PLAYER_HEALTH, this.playerHealth + data.amount);
      this.hud.updateHealth(this.playerHealth, this.MAX_PLAYER_HEALTH);
    });

    // Listen for bike landing events (air-time scoring)
    EventBus.on('bike:landed', (data) => {
      if (this.isDead) return;

      // Award points for significant air time (> 0.5 seconds)
      if (data.airTime > 0.5) {
        const airTimePoints = Math.floor(data.airTime * 100);
        this.hud.showAirTimeBonus(airTimePoints);

        // Emit score event
        EventBus.emit('score:added', {
          points: airTimePoints,
          reason: 'airtime',
        });
      }

      // Camera shake on hard landing
      if (data.velocity < -8) {
        const shakeIntensity = Math.min(Math.abs(data.velocity) / 20, 0.5);
        this.cameraController.shake(shakeIntensity, 0.2);
      }
    });

    // Listen for bike launch events
    EventBus.on('bike:launched', () => {
      // Could add visual/audio feedback here
      // e.g., play whoosh sound, show speed lines
    });

    // Listen for boss drone spawns to register them for collision
    EventBus.on('boss:droneSpawned', (data) => {
      const drone = data.drone as FlyingDrone;
      this.drones.push(drone);
      this.weaponManager.registerTarget(drone.getMesh());
    });
  }

  private onPlayerDeath(): void {
    this.isDead = true;
    this.respawnTimer = this.RESPAWN_DELAY;

    const pos = this.playerBike.getPosition();
    EventBus.emit('player:died', {
      position: { x: pos.x, y: pos.y, z: pos.z },
    });

    // Show death message (handled by HUD or a separate component)
    this.showDeathScreen();
  }

  private showDeathScreen(): void {
    const deathScreen = document.createElement('div');
    deathScreen.id = 'death-screen';
    deathScreen.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 200;
    `;

    const title = document.createElement('div');
    title.textContent = 'SYSTEM FAILURE';
    title.style.cssText = `
      color: #ff0000;
      font-size: 48px;
      font-weight: bold;
      text-shadow: 0 0 20px #ff0000;
      letter-spacing: 5px;
      margin-bottom: 20px;
    `;

    const respawnText = document.createElement('div');
    respawnText.id = 'respawn-timer';
    respawnText.textContent = 'REBOOTING...';
    respawnText.style.cssText = `
      color: #00ffff;
      font-size: 24px;
      text-shadow: 0 0 10px #00ffff;
      letter-spacing: 3px;
    `;

    deathScreen.appendChild(title);
    deathScreen.appendChild(respawnText);
    document.getElementById('ui-container')?.appendChild(deathScreen);
  }

  private respawnPlayer(): void {
    this.isDead = false;
    this.playerHealth = this.MAX_PLAYER_HEALTH;
    this.hud.updateHealth(this.playerHealth, this.MAX_PLAYER_HEALTH);

    // Reset player position
    // Note: Would need to add a reset method to PlayerBike for full respawn
    // For now, just restore health

    // Remove death screen
    document.getElementById('death-screen')?.remove();
  }

  private spawnTurrets(): void {
    const turretConfigs: TurretConfig[] = [
      {
        position: new THREE.Vector3(25, 0, 25),
        detectionRange: 40,
        attackRange: 35,
        fireRate: 2,
        damage: 10,
        rotationSpeed: 2,
        health: 80,
      },
      {
        position: new THREE.Vector3(-25, 0, 25),
        detectionRange: 40,
        attackRange: 35,
        fireRate: 2,
        damage: 10,
        rotationSpeed: 2,
        health: 80,
      },
      {
        position: new THREE.Vector3(25, 0, -25),
        detectionRange: 40,
        attackRange: 35,
        fireRate: 2.5,
        damage: 8,
        rotationSpeed: 2.5,
        health: 60,
      },
      {
        position: new THREE.Vector3(-25, 0, -25),
        detectionRange: 40,
        attackRange: 35,
        fireRate: 2.5,
        damage: 8,
        rotationSpeed: 2.5,
        health: 60,
      },
    ];

    for (const config of turretConfigs) {
      const turret = new Turret(
        this.sceneManager.scene,
        this.enemyProjectilePool,
        config
      );
      turret.setPlayer(this.playerBike.getMesh());
      this.turrets.push(turret);
      this.weaponManager.registerTarget(turret.getMesh());
    }
  }

  private spawnEnemyBikes(): void {
    // 4 enemy bikes with large patrol territories (~150 units)
    // They'll chase at 100 km/h when player enters their territory
    const enemyConfigs: EnemyBikeConfig[] = [
      // Northeast quadrant
      {
        position: new THREE.Vector3(80, 1, 80),
        patrolPoints: [
          new THREE.Vector3(120, 1, 120),
          new THREE.Vector3(-30, 1, 120),
          new THREE.Vector3(-30, 1, -30),
          new THREE.Vector3(120, 1, -30),
        ],
        detectionRange: 80,
        attackRange: 40,
        speed: 28, // Max speed 100 km/h
        fireRate: 1.8,
        damage: 10,
        health: 120,
      },
      // Southwest quadrant
      {
        position: new THREE.Vector3(-80, 1, -80),
        patrolPoints: [
          new THREE.Vector3(-120, 1, -120),
          new THREE.Vector3(30, 1, -120),
          new THREE.Vector3(30, 1, 30),
          new THREE.Vector3(-120, 1, 30),
        ],
        detectionRange: 80,
        attackRange: 40,
        speed: 28,
        fireRate: 1.8,
        damage: 10,
        health: 120,
      },
      // Northwest quadrant
      {
        position: new THREE.Vector3(-60, 1, 60),
        patrolPoints: [
          new THREE.Vector3(-100, 1, 100),
          new THREE.Vector3(50, 1, 100),
          new THREE.Vector3(50, 1, -50),
          new THREE.Vector3(-100, 1, -50),
        ],
        detectionRange: 80,
        attackRange: 40,
        speed: 28,
        fireRate: 1.8,
        damage: 10,
        health: 120,
      },
      // Southeast quadrant - faster, more aggressive
      {
        position: new THREE.Vector3(60, 1, -60),
        patrolPoints: [
          new THREE.Vector3(100, 1, -100),
          new THREE.Vector3(-50, 1, -100),
          new THREE.Vector3(-50, 1, 50),
          new THREE.Vector3(100, 1, 50),
        ],
        detectionRange: 90, // Slightly longer detection
        attackRange: 45,
        speed: 28,
        fireRate: 2.0, // Faster firing
        damage: 12,
        health: 100,
      },
    ];

    for (const config of enemyConfigs) {
      const enemyBike = new EnemyBike(
        this.sceneManager.scene,
        this.physicsWorld,
        this.enemyProjectilePool,
        config
      );
      enemyBike.setPlayer(this.playerBike.getMesh());
      this.enemyBikes.push(enemyBike);
      this.weaponManager.registerTarget(enemyBike.getMesh());
    }
  }

  private spawnDrones(): void {
    // Spawn drone swarms at various positions around the map
    const swarmPositions = [
      new THREE.Vector3(40, 0, 40),
      new THREE.Vector3(-40, 0, -40),
      new THREE.Vector3(60, 0, 0),
    ];

    for (const pos of swarmPositions) {
      const swarm = spawnDroneSwarm(
        this.sceneManager.scene,
        this.enemyProjectilePool,
        pos,
        this.playerBike.getMesh(),
        3 // 3 drones per swarm
      );

      for (const drone of swarm) {
        this.drones.push(drone);
        this.weaponManager.registerTarget(drone.getMesh());
      }
    }
  }

  private spawnTanks(): void {
    const tankPositions = [
      new THREE.Vector3(70, 0, 0),
      new THREE.Vector3(-70, 0, 0),
      new THREE.Vector3(0, 0, 70),
    ];

    for (const pos of tankPositions) {
      const tank = new HeavyTank(
        this.sceneManager.scene,
        this.enemyProjectilePool,
        {
          position: pos,
          targetRef: this.playerBike.getMesh(),
        }
      );
      this.tanks.push(tank);
      this.weaponManager.registerTarget(tank.getMesh());
    }
  }

  private spawnSniperTowers(): void {
    const towerPositions = [
      new THREE.Vector3(80, 0, 80),
      new THREE.Vector3(-80, 0, 80),
      new THREE.Vector3(80, 0, -80),
      new THREE.Vector3(-80, 0, -80),
    ];

    for (const pos of towerPositions) {
      const tower = new SniperTower(
        this.sceneManager.scene,
        this.enemyProjectilePool,
        {
          position: pos,
          targetRef: this.playerBike.getMesh(),
        }
      );
      this.sniperTowers.push(tower);
      this.weaponManager.registerTarget(tower.getMesh());
    }
  }

  private spawnBoss(): void {
    // Spawn the Mega Mech boss at a far corner
    this.megaMech = new MegaMech(
      this.sceneManager.scene,
      this.enemyProjectilePool,
      {
        position: new THREE.Vector3(100, 0, 100),
        targetRef: this.playerBike.getMesh(),
      }
    );
    this.weaponManager.registerTarget(this.megaMech.getMesh());
  }

  private spawnDestructibles(): void {
    // Create targets in a circle around spawn
    const targetPositions = [
      new THREE.Vector3(20, 0, 20),
      new THREE.Vector3(-20, 0, 20),
      new THREE.Vector3(20, 0, -20),
      new THREE.Vector3(-20, 0, -20),
      new THREE.Vector3(30, 0, 0),
      new THREE.Vector3(-30, 0, 0),
      new THREE.Vector3(0, 0, 30),
      new THREE.Vector3(0, 0, -30),
      new THREE.Vector3(40, 0, 40),
      new THREE.Vector3(-40, 0, 40),
      new THREE.Vector3(40, 0, -40),
      new THREE.Vector3(-40, 0, -40),
    ];

    const colors = [
      new THREE.Color(0x2a2a4e),
      new THREE.Color(0x1a1a3e),
      new THREE.Color(0x3a2a4e),
    ];

    const glowColors = [
      new THREE.Color(0xff00ff),
      new THREE.Color(0x00ffff),
      new THREE.Color(0x8000ff),
    ];

    for (let i = 0; i < targetPositions.length; i++) {
      const destructible = new Destructible(
        this.sceneManager.scene,
        this.physicsWorld,
        {
          position: targetPositions[i],
          size: new THREE.Vector3(
            1.5 + Math.random() * 1.5,
            2 + Math.random() * 2,
            1.5 + Math.random() * 1.5
          ),
          health: 30 + Math.floor(Math.random() * 50),
          points: 100 + Math.floor(Math.random() * 100),
          color: colors[i % colors.length],
          glowColor: glowColors[i % glowColors.length],
        }
      );

      this.destructibles.push(destructible);
      this.weaponManager.registerTarget(destructible.getMesh());
    }
  }

  private setupControls(): void {
    // Toggle camera mode with V
    this.inputManager.onKeyDown('KeyV', () => {
      this.cameraController.toggleMode();
    });

    // Weapon switching with scroll wheel
    window.addEventListener('wheel', (e) => {
      if (e.deltaY > 0) {
        this.weaponManager.nextWeapon();
      } else {
        this.weaponManager.previousWeapon();
      }
    });

    // Weapon switching with number keys
    this.inputManager.onKeyDown('Digit1', () => {
      this.weaponManager.selectWeapon(0);
    });
    this.inputManager.onKeyDown('Digit2', () => {
      this.weaponManager.selectWeapon(1);
    });

    // Reload with R
    this.inputManager.onKeyDown('KeyR', () => {
      this.weaponManager.reload();
    });

    // Open Mode Selection with M
    this.inputManager.onKeyDown('KeyM', () => {
      this.openModeSelect();
    });

    // Restart/Continue with Space
    this.inputManager.onKeyDown('Space', () => {
      this.handleSpacePress();
    });

    // Mode-specific: Restart with Enter (avoids conflict with R for Reload)
    this.inputManager.onKeyDown('Enter', () => {
      this.handleRestartPress();
    });

    // Mode-specific: Next with N
    this.inputManager.onKeyDown('KeyN', () => {
      this.handleNextPress();
    });

    // Pause menu with Escape
    this.inputManager.onKeyDown('Escape', () => {
      this.togglePause();
    });
  }

  private openModeSelect(): void {
    // Don't open menu if already open
    if (this.modeSelectMenu?.isVisible()) return;

    // Stop any current mode
    this.stopCurrentMode();

    this.modeSelectMenu?.show((mode) => {
      this.startMode(mode);
    });
  }

  private startMode(mode: GameModeType): void {
    this.stopCurrentMode();
    this.currentMode = mode;

    switch (mode) {
      case 'freeRoam':
        // Respawn free roam enemies
        this.spawnDestructibles();
        this.spawnTurrets();
        this.spawnEnemyBikes();
        this.spawnDrones();
        this.spawnTanks();
        this.spawnSniperTowers();
        this.spawnBoss();
        break;

      case 'wave':
        this.clearAllEnemies();
        this.isWaveModeActive = true;
        this.waveMode?.start();
        break;

      case 'timeTrial':
        this.clearAllEnemies();
        this.timeTrialMode?.start();
        break;

      case 'mission':
        this.clearAllEnemies();
        this.missionMode?.start();
        break;
    }
  }

  private stopCurrentMode(): void {
    this.waveMode?.stop();
    this.timeTrialMode?.stop();
    this.missionMode?.stop();
    this.isWaveModeActive = false;
    this.clearAllEnemies();
  }

  private handleSpacePress(): void {
    // Mission mode: start mission from briefing
    if (this.currentMode === 'mission' && this.missionMode?.getState() === 'briefing') {
      this.missionMode.startMission();
      return;
    }

    // Dead in wave mode: restart
    if (this.isDead && this.isWaveModeActive && this.waveMode) {
      this.respawnPlayer();
      this.waveMode.restart();
    }
  }

  private handleRestartPress(): void {
    if (this.currentMode === 'timeTrial' && this.timeTrialMode) {
      this.timeTrialMode.restartRace();
    } else if (this.currentMode === 'mission' && this.missionMode) {
      const state = this.missionMode.getState();
      if (state === 'completed' || state === 'failed') {
        this.missionMode.restartMission();
      }
    }
  }

  private handleNextPress(): void {
    if (this.currentMode === 'timeTrial' && this.timeTrialMode?.isRaceFinished()) {
      this.timeTrialMode.nextTrack();
    } else if (this.currentMode === 'mission' && this.missionMode?.getState() === 'completed') {
      this.missionMode.nextMission();
    }
  }

  private togglePause(): void {
    // Don't pause if mode select menu is open
    if (this.modeSelectMenu?.isVisible()) return;

    if (this.isPaused) {
      this.resumeGame();
    } else {
      this.pauseGame();
    }
  }

  private pauseGame(): void {
    this.isPaused = true;

    // Exit pointer lock so user can interact with menu
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }

    // Hide touch controls during pause
    this.inputManager.setTouchControlsVisible(false);

    // Create pause menu if not exists
    if (!this.pauseMenu) {
      this.pauseMenu = this.createPauseMenu();
      document.getElementById('ui-container')?.appendChild(this.pauseMenu);
    }

    this.pauseMenu.style.display = 'flex';
  }

  private resumeGame(): void {
    this.isPaused = false;

    if (this.pauseMenu) {
      this.pauseMenu.style.display = 'none';
    }

    // Show touch controls again
    if (MobileManager.isTouchDevice()) {
      this.inputManager.setTouchControlsVisible(true);
    }
  }

  private createPauseMenu(): HTMLElement {
    const menu = document.createElement('div');
    menu.id = 'pause-menu';
    menu.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.85);
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      padding-top: 40px;
      gap: 15px;
      z-index: 250;
      font-family: 'Courier New', monospace;
      pointer-events: auto;
      overflow-y: auto;
    `;

    menu.innerHTML = `
      <div style="color: #00ffff; font-size: 42px; font-weight: bold; text-shadow: 0 0 20px #00ffff; letter-spacing: 5px; margin-bottom: 15px;">
        PAUSED
      </div>
      <div id="pause-buttons" style="display: flex; flex-direction: column; gap: 12px;"></div>

      <!-- Audio Section -->
      <div style="margin-top: 20px; width: 400px;">
        <div style="color: #00ffff; font-size: 16px; font-weight: bold; letter-spacing: 3px; border-bottom: 1px solid #00ffff; padding-bottom: 8px; margin-bottom: 15px; text-align: center;">
          AUDIO
        </div>
        <div id="audio-controls" style="display: flex; flex-direction: column; gap: 12px;"></div>
      </div>

      <!-- Controls Section -->
      <div style="margin-top: 20px; width: 500px;">
        <div style="color: #00ffff; font-size: 16px; font-weight: bold; letter-spacing: 3px; border-bottom: 1px solid #00ffff; padding-bottom: 8px; margin-bottom: 15px; text-align: center;">
          CONTROLS
        </div>
        ${MobileManager.isTouchDevice() ? `
        <!-- Mobile Controls -->
        <div style="display: flex; gap: 40px; justify-content: center;">
          <div style="color: #888; font-size: 13px; line-height: 1.8;">
            <div style="color: #00ffff; font-weight: bold; margin-bottom: 8px;">MOVEMENT</div>
            <div><span style="color: #ffff00;">Left Joystick</span> &nbsp;Move bike</div>
            <div style="color: #00ffff; font-weight: bold; margin-top: 12px; margin-bottom: 8px;">GAME</div>
            <div><span style="color: #ffff00;">❚❚ Button</span> &nbsp;Pause</div>
            <div><span style="color: #ffff00;">♪ Button</span> &nbsp;Toggle Music</div>
          </div>
          <div style="color: #888; font-size: 13px; line-height: 1.8;">
            <div style="color: #00ffff; font-weight: bold; margin-bottom: 8px;">COMBAT</div>
            <div><span style="color: #ffff00;">Tap Right</span> &nbsp;Fire</div>
            <div><span style="color: #ffff00;">Double-Tap</span> &nbsp;Switch Weapon</div>
            <div><span style="color: #ffff00;">Auto</span> &nbsp;Reload</div>
          </div>
        </div>
        ` : `
        <!-- Desktop Controls -->
        <div style="display: flex; gap: 40px; justify-content: center;">
          <div style="color: #888; font-size: 13px; line-height: 1.8;">
            <div style="color: #00ffff; font-weight: bold; margin-bottom: 8px;">MOVEMENT</div>
            <div><span style="color: #ffff00;">W / ↑</span> &nbsp;Forward</div>
            <div><span style="color: #ffff00;">S / ↓</span> &nbsp;Backward</div>
            <div><span style="color: #ffff00;">A / ←</span> &nbsp;Turn Left</div>
            <div><span style="color: #ffff00;">D / →</span> &nbsp;Turn Right</div>
            <div style="color: #00ffff; font-weight: bold; margin-top: 12px; margin-bottom: 8px;">GAME</div>
            <div><span style="color: #ffff00;">ESC</span> &nbsp;Pause</div>
            <div><span style="color: #ffff00;">M</span> &nbsp;Mode Select</div>
            <div><span style="color: #ffff00;">V</span> &nbsp;Camera Mode</div>
          </div>
          <div style="color: #888; font-size: 13px; line-height: 1.8;">
            <div style="color: #00ffff; font-weight: bold; margin-bottom: 8px;">COMBAT</div>
            <div><span style="color: #ffff00;">Mouse</span> &nbsp;Aim</div>
            <div><span style="color: #ffff00;">Q / E</span> &nbsp;Aim Up/Down</div>
            <div><span style="color: #ffff00;">Click</span> &nbsp;Fire</div>
            <div><span style="color: #ffff00;">R</span> &nbsp;Reload</div>
            <div><span style="color: #ffff00;">1-2</span> &nbsp;Select Weapon</div>
            <div><span style="color: #ffff00;">Scroll</span> &nbsp;Switch Weapon</div>
          </div>
        </div>
        `}
      </div>

      <div style="color: #444; font-size: 11px; margin-top: 20px; padding-bottom: 20px;">
        ${MobileManager.isTouchDevice() ? 'Tap RESUME to continue' : 'Press ESC to resume'}
      </div>
    `;

    // Setup buttons
    const buttonContainer = menu.querySelector('#pause-buttons') as HTMLElement;

    const buttons = [
      { label: 'RESUME', action: () => this.resumeGame() },
      { label: 'MODE SELECT', action: () => { this.resumeGame(); this.openModeSelect(); } },
    ];

    for (const btn of buttons) {
      const button = document.createElement('button');
      button.textContent = btn.label;
      button.style.cssText = `
        background: rgba(0, 255, 255, 0.1);
        border: 2px solid #00ffff;
        color: #00ffff;
        padding: 12px 40px;
        font-size: 18px;
        font-weight: bold;
        letter-spacing: 3px;
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: 'Courier New', monospace;
        min-width: 280px;
      `;

      button.addEventListener('mouseenter', () => {
        button.style.background = 'rgba(0, 255, 255, 0.3)';
        button.style.transform = 'scale(1.02)';
        button.style.boxShadow = '0 0 20px #00ffff';
      });

      button.addEventListener('mouseleave', () => {
        button.style.background = 'rgba(0, 255, 255, 0.1)';
        button.style.transform = 'scale(1)';
        button.style.boxShadow = 'none';
      });

      button.addEventListener('click', (e) => {
        e.stopPropagation();
        btn.action();
      });

      buttonContainer.appendChild(button);
    }

    // Setup audio controls
    const audioContainer = menu.querySelector('#audio-controls') as HTMLElement;
    this.createAudioSliders(audioContainer);

    // Stop clicks from propagating
    menu.addEventListener('click', (e) => e.stopPropagation());

    return menu;
  }

  private createAudioSliders(container: HTMLElement): void {
    const musicVolume = audioManager.getMusicVolume();
    const isMuted = audioManager.isMutedState();

    // Music control row
    const musicRow = document.createElement('div');
    musicRow.style.cssText = 'display: flex; align-items: center; gap: 10px;';
    musicRow.innerHTML = `
      <span style="color: #888; font-size: 13px; width: 60px;">Music</span>
      <input type="range" id="music-slider" min="0" max="100" value="${musicVolume * 100}"
        style="flex: 1; height: 6px; cursor: pointer; accent-color: #00ffff;">
      <span id="music-value" style="color: #00ffff; font-size: 12px; width: 35px; text-align: right;">${Math.round(musicVolume * 100)}%</span>
      <button id="music-mute" style="
        background: ${isMuted ? 'rgba(255, 0, 0, 0.3)' : 'rgba(0, 255, 255, 0.2)'};
        border: 1px solid ${isMuted ? '#ff4444' : '#00ffff'};
        color: ${isMuted ? '#ff4444' : '#00ffff'};
        padding: 4px 8px;
        font-size: 11px;
        cursor: pointer;
        font-family: 'Courier New', monospace;
      ">${isMuted ? 'MUTED' : 'MUTE'}</button>
    `;
    container.appendChild(musicRow);

    // Sound effects control row (placeholder - uses same mute for now)
    const soundRow = document.createElement('div');
    soundRow.style.cssText = 'display: flex; align-items: center; gap: 10px;';
    soundRow.innerHTML = `
      <span style="color: #888; font-size: 13px; width: 60px;">Sound</span>
      <input type="range" id="sound-slider" min="0" max="100" value="100"
        style="flex: 1; height: 6px; cursor: pointer; accent-color: #00ffff;">
      <span id="sound-value" style="color: #00ffff; font-size: 12px; width: 35px; text-align: right;">100%</span>
      <button id="sound-mute" style="
        background: rgba(0, 255, 255, 0.2);
        border: 1px solid #00ffff;
        color: #00ffff;
        padding: 4px 8px;
        font-size: 11px;
        cursor: pointer;
        font-family: 'Courier New', monospace;
      ">MUTE</button>
    `;
    container.appendChild(soundRow);

    // Event listeners for music slider
    const musicSlider = container.querySelector('#music-slider') as HTMLInputElement;
    const musicValue = container.querySelector('#music-value') as HTMLElement;
    const musicMuteBtn = container.querySelector('#music-mute') as HTMLButtonElement;

    musicSlider.addEventListener('input', () => {
      const vol = parseInt(musicSlider.value) / 100;
      audioManager.setMusicVolume(vol);
      musicValue.textContent = `${musicSlider.value}%`;
    });

    musicMuteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const muted = audioManager.toggleMute();
      musicMuteBtn.textContent = muted ? 'MUTED' : 'MUTE';
      musicMuteBtn.style.background = muted ? 'rgba(255, 0, 0, 0.3)' : 'rgba(0, 255, 255, 0.2)';
      musicMuteBtn.style.borderColor = muted ? '#ff4444' : '#00ffff';
      musicMuteBtn.style.color = muted ? '#ff4444' : '#00ffff';
      // Update HUD audio indicator
      if (this.hud) {
        this.hud.updateAudioIndicator(muted);
      }
    });

    // Sound slider events (for future sound effect volume control)
    const soundSlider = container.querySelector('#sound-slider') as HTMLInputElement;
    const soundValue = container.querySelector('#sound-value') as HTMLElement;

    soundSlider.addEventListener('input', () => {
      soundValue.textContent = `${soundSlider.value}%`;
      // TODO: Implement sound effects volume when SoundEffects supports it
    });
  }

  private clearAllEnemies(): void {
    // Remove all free-roam enemies to prepare for wave mode
    for (const d of this.destructibles) {
      const mesh = d.getMesh();
      if (mesh.parent) mesh.parent.remove(mesh);
    }
    this.destructibles = [];

    for (const t of this.turrets) {
      const mesh = t.getMesh();
      if (mesh.parent) mesh.parent.remove(mesh);
    }
    this.turrets = [];

    for (const e of this.enemyBikes) {
      const mesh = e.getMesh();
      if (mesh.parent) mesh.parent.remove(mesh);
    }
    this.enemyBikes = [];

    for (const d of this.drones) {
      const mesh = d.getMesh();
      if (mesh.parent) mesh.parent.remove(mesh);
    }
    this.drones = [];

    for (const t of this.tanks) {
      const mesh = t.getMesh();
      if (mesh.parent) mesh.parent.remove(mesh);
    }
    this.tanks = [];

    for (const s of this.sniperTowers) {
      const mesh = s.getMesh();
      if (mesh.parent) mesh.parent.remove(mesh);
    }
    this.sniperTowers = [];

    if (this.megaMech) {
      const mesh = this.megaMech.getMesh();
      if (mesh.parent) mesh.parent.remove(mesh);
      this.megaMech = null;
    }
  }

  public start(): void {
    this.engine.start();
  }

  public stop(): void {
    this.engine.stop();
  }

  private fixedUpdate(dt: number): void {
    // Skip all updates when paused
    if (this.isPaused) return;

    // Handle respawn timer
    if (this.isDead) {
      // In special modes, don't auto-respawn - wait for player input
      const specialMode = this.currentMode !== 'freeRoam';
      if (!specialMode) {
        this.respawnTimer -= dt;
        if (this.respawnTimer <= 0) {
          this.respawnPlayer();
        }
      }
      // Still update mode UI while dead
      if (this.currentMode === 'wave' && this.waveMode) {
        this.waveMode.update(dt);
      } else if (this.currentMode === 'mission' && this.missionMode) {
        this.missionMode.update(dt);
      }
      return; // Don't update anything else while dead
    }

    // Process input and update player
    this.updatePlayerInput();

    // Update keyboard-based aiming
    this.inputManager.updateAiming(dt);

    // Handle firing
    this.handleFiring();

    // Step physics
    this.physicsWorld.step(dt);

    // Get ground height using multi-point sampling for better ramp detection
    const playerPos = this.playerBike.getPosition();
    const playerForward = this.playerBike.getForwardVector();
    const groundInfo = this.worldGenerator.getGroundHeightMultiPoint(
      playerPos,
      playerForward,
      2.0, // wheelBase
      1.0  // trackWidth
    );

    // Update player bike with ground height and ramp state
    this.playerBike.update(dt, groundInfo.height, groundInfo.onRamp);

    // Update game mode or enemies
    switch (this.currentMode) {
      case 'wave':
        if (this.waveMode) this.waveMode.update(dt);
        break;
      case 'timeTrial':
        if (this.timeTrialMode) this.timeTrialMode.update(dt);
        break;
      case 'mission':
        if (this.missionMode) this.missionMode.update(dt);
        break;
      default:
        this.updateEnemies(dt);
    }

    // Update enemy projectiles and check for player hits
    this.enemyProjectilePool.update(dt);
    this.checkEnemyProjectileHits();

    // Update weapon manager
    this.weaponManager.update(dt);

    // Update visual effects (particles, explosions)
    this.effectsManager.update(dt);

    // Update world (collectibles, ramps)
    this.worldGenerator.update(dt, this.playerBike.getPosition());

    // Update radar
    this.updateRadar();

    // Update HUD with speed and aim
    this.hud.updateSpeed(this.playerBike.getSpeedKmh());
    this.hud.updateAimPitch(this.inputManager.getAimPitch());

    // Update engine sound pitch based on speed
    soundEffects.updateEnginePitch(this.playerBike.getSpeed());

    // Clean up destroyed entities
    this.destructibles = this.destructibles.filter((d) => d.isAlive());
    this.turrets = this.turrets.filter((t) => t.isAlive());
    this.enemyBikes = this.enemyBikes.filter((e) => e.isAlive());
    this.drones = this.drones.filter((d) => d.isAlive());
    this.tanks = this.tanks.filter((t) => t.isAlive());
    this.sniperTowers = this.sniperTowers.filter((t) => t.isAlive());
  }

  private updateEnemies(dt: number): void {
    // Update turrets
    for (const turret of this.turrets) {
      turret.update(dt);
    }

    // Update enemy bikes
    for (const enemyBike of this.enemyBikes) {
      enemyBike.update(dt);
    }

    // Update drones
    for (const drone of this.drones) {
      drone.update(dt);
    }

    // Update tanks
    for (const tank of this.tanks) {
      tank.update(dt);
    }

    // Update sniper towers
    for (const tower of this.sniperTowers) {
      tower.update(dt);
    }

    // Update boss
    if (this.megaMech && this.megaMech.isAlive()) {
      this.megaMech.update(dt);
    }
  }

  private checkEnemyProjectileHits(): void {
    const playerPos = this.playerBike.getPosition();
    const playerHitRadius = 1.5;

    for (const projectile of this.enemyProjectilePool.getActiveProjectiles()) {
      const distance = projectile.mesh.position.distanceTo(playerPos);

      if (distance < playerHitRadius + 0.1) {
        // Player hit by enemy projectile
        EventBus.emit('player:damaged', {
          damage: projectile.damage,
          source: 'enemy',
        });

        this.enemyProjectilePool.despawn(projectile);
      }
    }
  }

  private updateRadar(): void {
    const playerPos = this.playerBike.getPosition();
    const playerRot = this.playerBike.getRotation();

    // Get player facing direction (yaw angle)
    const forward = new THREE.Vector3(0, 0, 1);
    forward.applyQuaternion(playerRot);
    const playerAngle = Math.atan2(forward.x, forward.z);

    this.radar.setPlayerPosition(playerPos, playerAngle);
    this.radar.clearBlips();

    // Add turret blips
    for (const turret of this.turrets) {
      if (turret.isAlive()) {
        this.radar.addBlip(turret.getMesh().position, 'enemy');
      }
    }

    // Add enemy bike blips
    for (const enemyBike of this.enemyBikes) {
      if (enemyBike.isAlive()) {
        this.radar.addBlip(enemyBike.getMesh().position, 'enemy');
      }
    }

    // Add drone blips
    for (const drone of this.drones) {
      if (drone.isAlive()) {
        this.radar.addBlip(drone.getPosition(), 'enemy');
      }
    }

    // Add tank blips
    for (const tank of this.tanks) {
      if (tank.isAlive()) {
        this.radar.addBlip(tank.getPosition(), 'enemy');
      }
    }

    // Add sniper tower blips
    for (const tower of this.sniperTowers) {
      if (tower.isAlive()) {
        this.radar.addBlip(tower.getPosition(), 'enemy');
      }
    }

    // Add boss blip (special marker)
    if (this.megaMech && this.megaMech.isAlive()) {
      this.radar.addBlip(this.megaMech.getPosition(), 'enemy');
    }

    // Add game mode enemies to radar
    if (this.currentMode === 'wave' && this.waveMode) {
      for (const pos of this.waveMode.getEnemyPositions()) {
        this.radar.addBlip(pos, 'enemy');
      }
    } else if (this.currentMode === 'mission' && this.missionMode) {
      for (const pos of this.missionMode.getEnemyPositions()) {
        this.radar.addBlip(pos, 'enemy');
      }
    }

    // Add destructible targets as targets
    for (const destructible of this.destructibles) {
      if (destructible.isAlive()) {
        this.radar.addBlip(destructible.getMesh().position, 'target');
      }
    }

    // Add collectibles as objectives
    for (const collectible of this.worldGenerator.getActiveCollectibles()) {
      this.radar.addBlip(collectible.getPosition(), 'objective');
    }

    this.radar.render();
  }

  private updatePlayerInput(): void {
    const input = this.inputManager;

    // Movement
    let engineForce = 0;
    let steerValue = 0;
    let braking = false;

    // Check for touch controls on mobile
    if (input.hasTouchControls()) {
      const touch = input.getTouchMovement();
      engineForce = touch.y; // Joystick Y = throttle (-1 to 1)
      steerValue = -touch.x; // Joystick X = steer (invert for correct direction)
      // No braking on touch (could add swipe down gesture later)

      // Touch firing
      this.isFiring = input.isTouchFiring();
    } else {
      // Keyboard controls
      if (input.isKeyPressed('KeyW') || input.isKeyPressed('ArrowUp')) {
        engineForce = 1;
      }
      if (input.isKeyPressed('KeyS') || input.isKeyPressed('ArrowDown')) {
        engineForce = -0.5; // Reverse is slower
      }
      if (input.isKeyPressed('KeyA') || input.isKeyPressed('ArrowLeft')) {
        steerValue = 1;
      }
      if (input.isKeyPressed('KeyD') || input.isKeyPressed('ArrowRight')) {
        steerValue = -1;
      }
      if (input.isKeyPressed('Space')) {
        braking = true;
      }

      // Track firing state (mouse button)
      this.isFiring = input.isMouseButtonPressed(0); // Left mouse button
    }

    // Apply to player bike
    this.playerBike.setInput(engineForce, steerValue, braking);
  }

  private handleFiring(): void {
    if (!this.isFiring) return;

    const weapon = this.weaponManager.getCurrentWeapon();
    if (!weapon) return;

    // Only fire if weapon is automatic or this is a new click
    if (!weapon.isAutomatic() && !this.inputManager.isMouseButtonPressed(0)) {
      return;
    }

    // Get firing origin and direction from player bike
    const bikePos = this.playerBike.getPosition();
    const bikeRot = this.playerBike.getRotation();

    // Fire from front of bike
    const fireOffset = new THREE.Vector3(0, 0.8, 1.5);
    fireOffset.applyQuaternion(bikeRot);
    const origin = bikePos.clone().add(fireOffset);

    // Fire in direction bike is facing
    const direction = new THREE.Vector3(0, 0, 1);
    direction.applyQuaternion(bikeRot);

    // Apply mouse aim pitch for vertical aiming (allows hitting flying targets)
    const aimPitch = this.inputManager.getAimPitch();
    if (aimPitch !== 0) {
      // Get the right vector to rotate around for pitch
      // Negate pitch because rotation around right axis is inverted
      const right = new THREE.Vector3(1, 0, 0);
      right.applyQuaternion(bikeRot);
      direction.applyAxisAngle(right, -aimPitch);
    }

    this.weaponManager.fire(origin, direction);
  }

  private render(alpha: number): void {
    // Update camera
    this.cameraController.update(alpha);

    // Render scene
    this.sceneManager.render();
  }
}
